import { useEffect, useRef, useState } from "react"

/**
 * Hook that calculates number of columns based on container width
 * Uses ResizeObserver for responsive updates
 */
export const useMasonryColumns = (minColumnWidth = 280, gap = 12) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [numColumns, setNumColumns] = useState(1)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const calculateColumns = (width: number) => {
      // Account for gaps: totalWidth = numCols * colWidth + (numCols - 1) * gap
      // Solve for numCols: numCols = (width + gap) / (minColumnWidth + gap)
      const cols = Math.floor((width + gap) / (minColumnWidth + gap))
      return Math.max(1, cols)
    }

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width
      setNumColumns(calculateColumns(width))
    })

    observer.observe(container)

    // Initial calculation
    setNumColumns(calculateColumns(container.offsetWidth))

    return () => observer.disconnect()
  }, [minColumnWidth, gap])

  return { containerRef, numColumns }
}

/**
 * Distributes items into columns using a "shortest column" algorithm
 * This creates a balanced masonry effect
 */
export const distributeIntoColumns = <T extends { totalExposure: number }>(
  items: T[],
  numColumns: number
): T[][] => {
  if (numColumns <= 0) return [items]

  // Initialize empty columns with height tracking
  const columns: T[][] = Array.from({ length: numColumns }, () => [])
  const columnHeights: number[] = Array(numColumns).fill(0)

  // Estimate card height based on number of companies (rough approximation)
  const estimateHeight = (item: T): number => {
    const companies = (item as any).companies?.length || 1
    return 50 + companies * 35 // header + rows
  }

  // Add each item to the shortest column
  items.forEach(item => {
    const shortestColIndex = columnHeights.indexOf(Math.min(...columnHeights))
    columns[shortestColIndex].push(item)
    columnHeights[shortestColIndex] += estimateHeight(item)
  })

  return columns
}
